import React, { useCallback } from 'react';
import { useQueryNavigation } from '@/hooks/shared/useNavigateWithQuery';
import { ASSETS_TYPE, FILTER_PARAMS } from '@/types/shared/navigation-tabs.enums';
import { BaseCard } from '@/components/uiKit/Cards/BaseCard';
import { REGULAR_DATE_FORMAT } from '@/const';
import { format } from 'date-fns';
import { SiteDataType } from '@/hooks';
import { AssetsCard } from './AssetsCard';
import { generateTestId } from '@/utils/shared/generateTestIds';

interface SitePanelProps {
  site: SiteDataType;
}

const SitePanelComponent = ({ site }: SitePanelProps) => {
  const {
    currentParams: { assetsType },
  } = useQueryNavigation();

  const { navigateWithQuery } = useQueryNavigation();

  const handleNavigate = useCallback((type: ASSETS_TYPE) => {
    const replaceParams = {
      [FILTER_PARAMS.ASSETS_TYPE]: type,
    };
    navigateWithQuery(`/sites/${site.site_id}/machines`, {
      excludeParams: ['search'],
      replaceParams,
    });
  }, []);

  const latestDiagnosticDate =
    assetsType && site.assets_categories[assetsType.split('-')[0] as 'all' | 'fixed' | 'mobile'].latest_diagnostic_date;

  const title = (
    <div className="flex flex-col gap-[2px] font-medium mb-1">
      <div className="text-lg" data-testid={generateTestId(`site_name_${site.site_name}`)}>
        {site.site_name}
      </div>
      <div className="text-gray-500 dark:text-gray-400" data-testid={generateTestId(`last_verified_${site.site_name}`)}>
        {'Last verified: ' +
          (latestDiagnosticDate ? format(new Date(latestDiagnosticDate), REGULAR_DATE_FORMAT) : 'No data')}
      </div>
    </div>
  );

  return (
    <BaseCard variant="wrapper" title={title} classNames={{ content: 'p-0 bg-inherit' }}>
      <div className="flex w-full gap-2">
        {(assetsType === ASSETS_TYPE.ALL || assetsType === ASSETS_TYPE.FIXED) && (
          <AssetsCard
            totalAssets={site.assets_categories.fixed.total_assets}
            chartData={site.fixed_chart_data}
            type="fixed"
            view={assetsType === ASSETS_TYPE.ALL ? 'half' : 'full'}
            handleClick={() => handleNavigate(ASSETS_TYPE.FIXED)}
            siteName={site.site_name}
          />
        )}
        {(assetsType === ASSETS_TYPE.ALL || assetsType === ASSETS_TYPE.MOBILE) && (
          <AssetsCard
            totalAssets={site.assets_categories.mobile.total_assets}
            chartData={site.mobile_chart_data}
            type="mobile"
            view={assetsType === ASSETS_TYPE.ALL ? 'half' : 'full'}
            handleClick={() => handleNavigate(ASSETS_TYPE.MOBILE)}
            siteName={site.site_name}
          />
        )}
      </div>
    </BaseCard>
  );
};

export const SitePanel = React.memo(SitePanelComponent);
